import { MailSendForm } from '@/components/_common';
import { useEmployee } from '@/core/domain/enterprise/Employees/Employees.hooks';
import { useMailConfiguration } from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.hooks';
import { SenderType } from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.types';
import { useMailSend } from '@/core/domain/mail/MailControler/MailControler.hooks';
import {
  MailMessageType,
  MailSendingForm,
} from '@/core/domain/mail/MailControler/MailControler.types';
import mailBase64ToUrl from '@/utils/functions/mailBase64ToUrl';
import { getUserFromLocalStorage } from '@/utils/user';
import { Alert, Form, Modal, ModalProps } from 'antd';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWorkorderChecklistContext } from '../../../WorkorderChecklistProvider';
import { WorkorderChecklistItemType } from '../../../WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import { MailTypeEnum } from '@/core/domain/mail/MailHistory/MailHistory.types';
import { useReportTokenGenerate } from '@/core/domain/reports/reportToken/reportToken.hooks';
import { ReportTokenType } from '@/core/domain/reports/reportToken/reportToken.types';
import { useWorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';

type Props = Omit<ModalProps, 'onCancel'> & {
  onCancel: VoidFunction;
};

const ChecklistMailSendModal: React.FC<Props> = (props) => {
  const { checklistItemId, workorderId, checklistItemType } =
    useWorkorderChecklistContext();
  const [uploading, setUploading] = useState(false);

  const workOrderDetail = useWorkOrder(workorderId, props.open);
  const companyName = workOrderDetail.data?.company?.name || '';

  const [form] = Form.useForm<MailSendingForm>();
  const { employeeId, userId } = getUserFromLocalStorage();
  const { data: employee } = useEmployee(Number(employeeId));
  const mailSend = useMailSend();

  const reportTokenGenerate = useReportTokenGenerate();

  const { data: mailConfig } = useMailConfiguration(
    {
      sender: SenderType.user,
    },
    props.open,
  );

  useEffect(() => {
    if (mailConfig?.signature && props.open) {
      form.setFieldValue('message', mailConfig.signature);
    }
  }, [mailConfig, props.open]);

  async function handleSubmit() {
    let newMessage = '';

    try {
      const values = await form.validateFields();

      if (!employee) return;

      const href = `${window.location.origin}/r/c/checklist`;

      const checklistToken = await reportTokenGenerate.mutateAsync({
        params: JSON.stringify({
          ...(checklistItemType === WorkorderChecklistItemType.Diagnostic
            ? { diagnosticId: checklistItemId }
            : { workOrderComplementId: checklistItemId }),
        }),
        reportType: ReportTokenType.CHECKLIST,
      });

      const hrefParams = new URLSearchParams({ token: checklistToken.token });

      newMessage = await mailBase64ToUrl(values.message, setUploading);

      const message = `
      ${newMessage}
      <br />
      <hr />
      <br />
      <a href="${href}?${hrefParams}" target="_black" alt="PDF do checklist">Visualizar Checklist</a>
      `;

      await mailSend.mutateAsync({
        ...values,
        workorderId: workorderId ? String(workorderId) : '',
        mailType: MailTypeEnum.CHECKLIST,
        messageType: MailMessageType.Html,
        message: message,
        userId: String(userId),
        senderId: String(userId),
        sender: SenderType.user,
        from: mailConfig?.mail || '',
        fromName: `${companyName} - ${employee?.name}`,
      });

      handleClose();
    } catch {
      form.setFieldValue('message', newMessage);
    }
  }

  function handleClose() {
    form.resetFields();

    !!props?.onCancel && props.onCancel();
  }

  return (
    <Modal
      {...props}
      width={800}
      title="Novo e-mail"
      cancelText="Descartar"
      okText="Enviar"
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={mailSend.isLoading || uploading}
      okButtonProps={{ disabled: !mailConfig }}
    >
      {!mailConfig && (
        <Alert
          type="info"
          description={
            <>
              <p>Necessário configurar os dados de e-mail</p>
              <Link to="/app/settings/emailSettings">Configurar email</Link>
            </>
          }
          style={{ marginBottom: 24 }}
        />
      )}
      <MailSendForm form={form} workorderId={workorderId} />
    </Modal>
  );
};

export default ChecklistMailSendModal;
